'use client';

// MÉCANIQUE d'un panneau flottant, sans aucun rendu : ouverture, fermeture au clic
// extérieur, fermeture à Échap, focus rendu au déclencheur.
//
// Séparée du rendu à dessein. Le menu d'une carte est une liste d'actions ; le panneau de
// filtres contient des champs de date, un interrupteur et deux boutons. Les faire tenir dans
// un même composant aurait demandé une poignée de drapeaux pour couvrir trois usages — pire
// que trois copies honnêtes. Ils partagent donc le COMPORTEMENT, chacun garde son rendu.
//
// EXCLUSIVITÉ MUTUELLE, gratuite : ouvrir un second panneau suppose de cliquer sur son
// déclencheur, qui est à l'extérieur du premier — dont le gestionnaire de clic extérieur le
// referme. Aucune coordination entre panneaux n'est nécessaire.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface Popover {
  open: boolean;
  /** À poser sur l'élément qui englobe déclencheur ET panneau. */
  hostRef: React.RefObject<HTMLDivElement | null>;
  /** À poser sur le bouton déclencheur, pour lui rendre le focus à la fermeture. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  toggle: (e?: { preventDefault(): void; stopPropagation(): void }) => void;
  /** Ferme et REND LE FOCUS au déclencheur — le chemin normal. */
  close: () => void;
  /** Ferme sans toucher au focus : pour un clic extérieur, où il est déjà ailleurs. */
  dismiss: () => void;
}

export function usePopover(): Popover {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const dismiss = useCallback(() => setOpen(false), []);

  const toggle = useCallback(
    (e?: { preventDefault(): void; stopPropagation(): void }) => {
      // Un panneau posé sur une carte cliquable : sans cela, l'ouvrir ouvrirait la carte.
      e?.preventDefault();
      e?.stopPropagation();
      setOpen((v) => !v);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent) => {
      if (hostRef.current && !hostRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Empêche un parent d'interpréter la même touche (fermer une fenêtre, vider un champ).
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return { open, hostRef, triggerRef, toggle, close, dismiss };
}
